import type { GameState, MatchResult } from './types'
import { calculateCoachingInfluence } from './matchProbability'
import { contextPerformanceMargin } from './matchContext'
import { calculateSelectionInfluence } from './selectionInfluence'

interface TacticalEffect {
  ourDelta: number
  opponentDelta: number
  metric: keyof MatchResult['tacticalAnalysis']
  metricDelta: number
  explanation: string
}

const EFFECTS: Record<string, TacticalEffect> = {
  'D6.1-B': { ourDelta: 3, opponentDelta: 2, metric: 'ballMovementEffectiveness', metricDelta: 7, explanation: 'Direct ball movement gained territory faster but increased turnover exposure.' },
  'D6.1-C': { ourDelta: -2, opponentDelta: -4, metric: 'ballMovementEffectiveness', metricDelta: 3, explanation: 'Patient possession reduced transition risk but slowed scoring entries.' },
  'D6.2-B': { ourDelta: 1, opponentDelta: 0, metric: 'widthEffectiveness', metricDelta: -3, explanation: 'Balanced width protected more options without fully stretching the opposition.' },
  'D6.2-C': { ourDelta: 4, opponentDelta: 4, metric: 'widthEffectiveness', metricDelta: -10, explanation: 'Corridor use created faster attacks but exposed Kingsport when possession turned over.' },
  'D6.3-B': { ourDelta: 1, opponentDelta: 1, metric: 'contestEffectiveness', metricDelta: 2, explanation: 'A balanced contest approach traded some structure for extra numbers at the ball.' },
  'D6.3-C': { ourDelta: 4, opponentDelta: 5, metric: 'contestEffectiveness', metricDelta: 8, explanation: 'Maximum pressure created repeat chances but left less protection outside the contest.' },
  'D6.4-B': { ourDelta: -2, opponentDelta: -5, metric: 'defensiveEffectiveness', metricDelta: 8, explanation: 'A deep defensive line protected space behind but conceded territory.' },
  'D6.4-C': { ourDelta: 4, opponentDelta: 6, metric: 'defensiveEffectiveness', metricDelta: -8, explanation: 'The aggressive line compressed the ground but conceded dangerous space behind.' },
  'D6.5-B': { ourDelta: -2, opponentDelta: -4, metric: 'tempoEffectiveness', metricDelta: -7, explanation: 'Controlled tempo reduced the opponent’s run but limited Kingsport’s scoring volume.' },
  'D6.5-C': { ourDelta: 5, opponentDelta: 6, metric: 'tempoEffectiveness', metricDelta: 9, explanation: 'Relentless tempo increased scoring opportunities and late fatigue risk for both sides.' },
  'D7-B': { ourDelta: 2, opponentDelta: 1, metric: 'tempoEffectiveness', metricDelta: 4, explanation: 'The Coach authorised an earlier youth injection to add running power.' },
  'D7-C': { ourDelta: 3, opponentDelta: 2, metric: 'contestEffectiveness', metricDelta: 5, explanation: 'The trailing-game pressure instruction created extra contests with reduced protection.' },
  'D7-D': { ourDelta: -1, opponentDelta: 1, metric: 'defensiveEffectiveness', metricDelta: -3, explanation: 'Removing the conditional instruction reduced adaptability when the match shifted.' },
}

const adjustedScore = (score: MatchResult['ourScore'], delta: number) => {
  const total = Math.max(0, score.total + delta)
  return { goals: Math.floor(total / 6), behinds: total % 6, total }
}

const splitAdjustedScore = (score: MatchResult['ourScore']) => Array.from({ length: 4 }, (_, index) => ({
  goals: Math.floor(score.goals / 4) + (index < score.goals % 4 ? 1 : 0),
  behinds: Math.floor(score.behinds / 4) + (index < score.behinds % 4 ? 1 : 0),
}))

const clampMetric = (value: number) => Math.max(0, Math.min(100, value))

export const applyTacticalOverrides = (base: MatchResult, state: GameState): MatchResult => {
  const effects = state.tactics
    .filter(tactic => tactic.currentChoice !== tactic.staffChoice)
    .map(tactic => {
      const selected = EFFECTS[`${tactic.id}-${tactic.currentChoice}`]
      const staff = EFFECTS[`${tactic.id}-${tactic.staffChoice}`]
      const reference = selected ?? staff
      if (!reference) return null
      const selectedLabel = tactic.options.find(option => option.letter === tactic.currentChoice)?.label ?? tactic.currentChoice
      const staffLabel = tactic.options.find(option => option.letter === tactic.staffChoice)?.label ?? tactic.staffChoice
      return {
        ourDelta: (selected?.ourDelta ?? 0) - (staff?.ourDelta ?? 0),
        opponentDelta: (selected?.opponentDelta ?? 0) - (staff?.opponentDelta ?? 0),
        metric: reference.metric,
        metricDelta: (selected?.metricDelta ?? 0) - (staff?.metricDelta ?? 0),
        explanation: `Coach changed ${tactic.dimension} from ${staffLabel} to ${selectedLabel}. ${selected?.explanation ?? 'The side returned to the neutral tactical option.'}`,
      } satisfies TacticalEffect
    })
    .filter((effect): effect is TacticalEffect => Boolean(effect))
  if (state.conditionalInstruction?.id && state.conditionalInstruction.id !== 'D7-A' && EFFECTS[state.conditionalInstruction.id]) {
    effects.push(EFFECTS[state.conditionalInstruction.id])
  }
  const researchFindings = state.researchPlan.topics.filter(topic => topic.isSelected).map(topic => {
    const selectedChoice = topic.tacticId === 'D7'
      ? state.conditionalInstruction?.id.slice(-1)
      : state.tactics.find(tactic => tactic.id === topic.tacticId)?.currentChoice
    return { topic, aligned: selectedChoice === topic.recommendedChoice }
  })
  const committedContext = state.season?.matchContexts?.[state.week?.fixtureId ?? '']
  if (effects.length === 0 && researchFindings.length === 0 && !committedContext) return base

  const tacticalAnalysis = { ...base.tacticalAnalysis }
  let ourDelta = 0
  let opponentDelta = 0
  for (const effect of effects) {
    ourDelta += effect.ourDelta
    opponentDelta += effect.opponentDelta
    const current = tacticalAnalysis[effect.metric]
    if (typeof current === 'number') tacticalAnalysis[effect.metric] = clampMetric(current + effect.metricDelta) as never
  }
  const explanations = effects.map(effect => effect.explanation)
  const researchExplanations = researchFindings.map(({ topic, aligned }) => aligned
    ? `Research → ${topic.tacticalAdvice} The match plan applied that finding.`
    : `Research identified ${topic.domain.toLowerCase()}, but the selected plan did not apply its recommended response.`)
  for (const { topic, aligned } of researchFindings) {
    if (!aligned) continue
    ourDelta += 1
    opponentDelta -= 1
    const metric = topic.tacticId === 'D7'
      ? 'contestEffectiveness'
      : EFFECTS[`${topic.tacticId}-${topic.recommendedChoice}`]?.metric
    if (metric) {
      const current = tacticalAnalysis[metric]
      if (typeof current === 'number') tacticalAnalysis[metric] = clampMetric(current + 2) as never
    }
  }
  const alignedResearchCount = researchFindings.filter(finding => finding.aligned).length
  const selection = calculateSelectionInfluence(state)
  const rawMarginEdge = ourDelta - opponentDelta + selection.rawSelectionEdge
  const rawScoringVolume = ourDelta + opponentDelta
  const season = state.season
  const fixture = season?.fixtures.find(candidate =>
    candidate.round === state.round
    && (candidate.homeTeamId === season.userTeamId || candidate.awayTeamId === season.userTeamId))
  const seed = committedContext?.seed ?? `${season?.seed ?? state.seed}:${fixture?.id ?? `round-${state.round}`}`
  const influence = calculateCoachingInfluence({
    rawMarginEdge,
    interventionCount: effects.length + selection.selectionChanges,
    alignedResearchCount,
    tacticalCohesion: state.club.tacticalCohesion,
    seed,
  })
  const opponentPlan = committedContext?.opponentPlan
  const opponentScoringVolume = opponentPlan?.approach === 'aggressive' ? 4 : opponentPlan?.approach === 'conservative' ? -3 : 0
  const scoringVolume = 6 * Math.tanh(rawScoringVolume / 6) + opponentScoringVolume
  const performanceMargin = committedContext ? contextPerformanceMargin(committedContext) : 0
  const realisedMarginInfluence = influence.realisedMarginEdge + selection.readinessMargin - (opponentPlan?.realisedMarginEdge ?? 0) + performanceMargin
  const roundedMarginInfluence = Math.round(realisedMarginInfluence)
  const roundedScoringVolume = Math.round(scoringVolume)
  const ourAdjustment = Math.round((roundedScoringVolume + roundedMarginInfluence) / 2)
  const opponentAdjustment = ourAdjustment - roundedMarginInfluence
  const influenceExplanation = influence.interventionLevel === 'staff'
    ? 'The staff plan remained the stable baseline.'
    : influence.interventionLevel === 'heavy'
      ? `Heavy Coach intervention produced diminishing expected returns and ${influence.executionRisk > 0 ? 'higher execution risk' : 'controlled execution risk'}.`
      : 'Focused Coach intervention changed the expected margin with controlled execution risk.'
  const opponentExplanation = opponentPlan ? `Opposition Coach: ${opponentPlan.explanation}` : ''
  const executionExplanation = committedContext
    ? committedContext.kingsportPerformance - committedContext.opponentPerformance > 0.6
      ? 'Match-day execution favoured Kingsport.'
      : committedContext.opponentPerformance - committedContext.kingsportPerformance > 0.6
        ? `Match-day execution favoured ${state.opponent.name}.`
        : 'Match-day execution was broadly even.'
    : ''
  tacticalAnalysis.summary = [
    base.tacticalAnalysis.summary,
    explanations.length ? `Coach overrides: ${explanations.join(' ')}` : '',
    researchExplanations.length ? `Preparation: ${researchExplanations.join(' ')}` : '',
    `Selection: ${selection.explanation}`,
    `Plan influence: ${influenceExplanation}`,
    opponentExplanation,
    executionExplanation,
  ].filter(Boolean).join(' ')

  const ourScore = adjustedScore(base.ourScore, ourAdjustment)
  const opponentScore = adjustedScore(base.opponentScore, opponentAdjustment)
  const ourQuarters = splitAdjustedScore(ourScore)
  const opponentQuarters = splitAdjustedScore(opponentScore)
  return {
    ...base,
    ourScore,
    opponentScore,
    quarters: base.quarters.map((quarter, index) => ({ ...quarter, ourScore: ourQuarters[index], opponentScore: opponentQuarters[index] })),
    tacticalAnalysis,
    momentumShifts: [
      ...base.momentumShifts,
      ...(explanations.length ? [{ quarter: 2, time: 'Coach plan', description: `${explanations.join(' ')} ${influenceExplanation}`, impact: influence.realisedMarginEdge >= 0 ? 'positive' as const : 'negative' as const }] : []),
      ...researchFindings.map(({ topic, aligned }) => ({
        quarter: 1,
        time: 'Preparation',
        description: aligned ? `Research → tactic: ${topic.tacticalAdvice}` : `Unused research: ${topic.domain} was understood, but its recommended response was not selected.`,
        impact: aligned ? 'positive' as const : 'neutral' as const,
      })),
      ...(opponentPlan ? [{
        quarter: 3,
        time: 'Opposition adjustment',
        description: opponentPlan.explanation,
        impact: opponentPlan.realisedMarginEdge > 0 ? 'negative' as const : 'neutral' as const,
      }] : []),
    ],
  }
}
